import { ProctoringOption } from "@verity/proctoring-option/types";
import React, { ReactNode, useEffect, useId, useState } from "react";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import {
	CoreInfoIndicator,
	CoreMenuItem,
	CorePaper,
	CoreRadioGroup,
	CoreRequiredIcon,
	CoreSelect,
	CoreSkeletonWrapper,
	CoreTextField,
	VerityColorLogo,
} from "../../core";
import { Box, InputLabel, Tooltip, Typography, TypographyProps } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import UnsupportedModal from "../edit-quiz-modals/unsupportedModal";
import styles from "./ThirdPartyStep.module.css";
import { ThirdPartyPlatform } from "../../types/common";
import { Trans } from "react-i18next";
import McGrawHill from "../images/third-party/McGraw-Hill.svg?react";
import Aleks from "../images/third-party/Aleks.svg?react";
import Pearson from "../images/third-party/Pearson.svg?react";
import Cengage from "../images/third-party/Cengage.svg?react";
import WWNorton from "../images/third-party/WWNorton.svg?react";
import Webwork from "../images/third-party/Webwork.svg?react";
import ZyBooks from "../images/third-party/ZyBooks.svg?react";
import Others from "../images/third-party/Others.svg?react";
import Wiley from "../images/third-party/Wiley.svg?react";
import UCertify from "../images/third-party/uCertify.svg?react";
import MyOpenMath from "../images/third-party/MyOpenMath.svg?react";
import Macmillan from "../images/third-party/Macmillan.svg?react";
import LabyrinthLearning from "../images/third-party/LabyrinthLearning.svg?react";
import VHL from "../images/third-party/VHL.svg?react";
import AtomicJolt from "../images/third-party/AtomicJolt.svg?react";
import passImage from "../images/check-pass.svg";
import failImage from "../images/check-fail.svg";
import { Platform } from "@verity/url-matcher";
import { EditQuizPageProps } from "../editQuiz";
import StepDescription, { StepWrapper } from "./CommonStepComponents";

type ThirdPartyRowProps = {
	loading?: boolean;
	createControl: (id: string) => ReactNode;
	header: ReactNode;
	description: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	descriptionProps?: Omit<TypographyProps, "children">;
};

function ThirdPartyRow({
	createControl,
	description,
	header,
	hint,
	loading,
	required,
	descriptionProps,
}: ThirdPartyRowProps) {
	const id = useId();
	return (
		<CoreSkeletonWrapper loading={loading} height={250}>
			<div className={styles.thirdPartyRow}>
				<div className={styles.thirdPartyLabelWrapper}>
					<div className={styles.titleRow}>
						<Typography variant="h3" id={id}>
							{header}
						</Typography>
						{required && <CoreRequiredIcon />}
						{hint && <CoreInfoIndicator size={20} hint={hint} />}
					</div>
					<Typography {...descriptionProps}>{description}</Typography>
				</div>
				{createControl(id)}
			</div>
		</CoreSkeletonWrapper>
	);
}

export const THIRD_PARTY_IMAGE_MAP: Record<Platform, React.ReactNode> = {
	[Platform.ALEKS]: <Aleks />,
	[Platform.MCGRAW_HILL]: <McGrawHill />,
	[Platform.PEARSON]: <Pearson />,
	[Platform.CENGAGE]: <Cengage />,
	[Platform.WILEY]: <Wiley />,
	[Platform.MY_OPEN_MATH]: <MyOpenMath />,
	[Platform.WW_NORTON]: <WWNorton />,
	[Platform.MACMILLAN]: <Macmillan />,
	[Platform.LABYRINTH]: <LabyrinthLearning />,
	[Platform.U_CERTIFY]: <UCertify />,
	[Platform.WEBWORK]: <Webwork />,
	[Platform.VHL]: <VHL />,
	[Platform.ZYBOOKS]: <ZyBooks />,
	[Platform.ATOMIC]: <AtomicJolt />,
	//TEMP IMAGE
	[Platform.WILLOLABS]: <Others />,
	[Platform.DEFAULT]: <Others />,
};

type ThirdPartyStepProps = {
	/**If first time configuring the quiz */
	isFirstTime: boolean;
	platforms: ThirdPartyPlatform[];
	selectedPlatformId: number | undefined | null;
	setSelectedPlatformId: (platform: number | undefined | null) => void;
	thirdPartyPassword: string | undefined | null;
	setThirdPartyPassword: (url: string | null) => void;
	loading?: boolean;
} & Required<Pick<EditQuizPageProps, "setProctoringOption" | "proctoringOption">>;

export default function ThirdPartyStep(props: ThirdPartyStepProps) {
	const {
		thirdPartyPassword,
		platforms,
		selectedPlatformId,
		setSelectedPlatformId,
		setThirdPartyPassword,
		isFirstTime,
		proctoringOption,
		setProctoringOption,
		loading,
	} = props;

	const [showUnsupportedModal, setShowUnsupportedModal] = useState(false);
	const [hasPassword, setHasPassword] = useState<boolean>(() => {
		// Undefined means user has yet to made choice
		const hasUserMadeChoice = thirdPartyPassword !== undefined;

		if (isFirstTime && !hasUserMadeChoice) {
			// UI team wants password to default to true on first time
			return true;
		}

		return !!thirdPartyPassword;
	});
	const [enableAnyway, setEnableAnyway] = useState(thirdPartyPassword ? true : false);
	const selectedPlatform =
		typeof selectedPlatformId === "number" ? platforms.find((p) => p.platformId === selectedPlatformId) : undefined;

	return (
		<>
			<StepWrapper>
				<div className={styles.thirdPartyContainer}>
					<StepDescription loading={loading}>
						{i18nPackage.t("editQuizPage.thirdPartyAssessment.description")}
					</StepDescription>
					<ThirdPartyRow
						loading={loading}
						description={i18nPackage.t("editQuizPage.thirdPartyAssessment.platformDesc")}
						header={i18nPackage.t("editQuizPage.thirdPartyAssessment.platformTitle")}
						hint={i18nPackage.t("editQuizPage.thirdPartyAssessment.platformTooltip")}
						createControl={(id) => (
							<CoreSelect
								value={selectedPlatformId ?? ""}
								renderValue={(value) => {
									const platform =
										typeof value === "number" ? platforms.find((p) => p.platformId === value) : undefined;
									const platformKey = platform ? (platform.platformId as Platform) : undefined;

									return (
										<div className={styles.selectLabelWrapper}>
											<Typography component="span">
												{platform
													? platform.platformName
													: i18nPackage.t("editQuizPage.thirdPartyAssessment.platformPlaceholder")}
											</Typography>
											{platformKey && platformKey in THIRD_PARTY_IMAGE_MAP ? THIRD_PARTY_IMAGE_MAP[platformKey] : null}
										</div>
									);
								}}
								onChange={(e) => {
									setSelectedPlatformId(Number(e.target.value));
								}}
								inputProps={{
									"aria-labelledby": id,
								}}
								MenuProps={{
									PaperProps: {
										sx: {
											maxHeight: "200px",
										},
									},
								}}
								className={styles.platformSelect}
							>
								{platforms.map((platform) => {
									const platformKey = platform.platformId as Platform;

									return (
										<CoreMenuItem key={platform.platformId} value={platform.platformId}>
											<div className={styles.selectLabelWrapper}>
												<Typography component="span">{platform.platformName}</Typography>
												{platformKey in THIRD_PARTY_IMAGE_MAP ? THIRD_PARTY_IMAGE_MAP[platformKey] : null}
											</div>
										</CoreMenuItem>
									);
								})}
							</CoreSelect>
						)}
					/>
					{selectedPlatform && (
						<>
							<ThirdPartyRow
								loading={loading}
								header={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordSwitchTitle")}
								description={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordSwitchDesc")}
								createControl={(id) => (
									<CoreRadioGroup
										radioOptions={[
											{ label: i18nPackage.t("editQuizPage.thirdPartyAssessment.yes"), value: "true" },
											{ label: i18nPackage.t("editQuizPage.thirdPartyAssessment.no"), value: "false" },
										]}
										onChange={(_, value) => {
											const hasPassword = value === "true";
											setHasPassword(hasPassword);
											if (!hasPassword) {
												setThirdPartyPassword(null);
											}
										}}
										selectedValue={String(hasPassword)}
										radioGroupAriaLabelledby={id}
										sx={{ paddingLeft: "9px" }}
									/>
								)}
							/>
							{hasPassword && (
								<ThirdPartyRow
									loading={loading}
									header={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordTitle")}
									description={
										<Trans
											i18n={i18nPackage}
											i18nKey={"editQuizPage.thirdPartyAssessment.passwordDesc"}
											components={{ b: <strong /> }}
											values={{ platform: selectedPlatform.platformName }}
										/>
									}
									descriptionProps={{ component: "span" }}
									createControl={() => (
										<CoreTextField
											onChange={(e) => {
												if (proctoringOption.disableRightClick && !enableAnyway && !thirdPartyPassword) {
													setShowUnsupportedModal(true);
												} else {
													setThirdPartyPassword(e.target.value);
												}
											}}
											value={thirdPartyPassword}
											label={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordPlaceholder")}
										/>
									)}
									required
								/>
							)}
							{!hasPassword && (
								<div className={styles.noPasswordContainer}>
									<VerityColorLogo height={28} width={28} />
									<Typography component={"span"}>
										<Trans
											i18n={i18nPackage}
											i18nKey={"editQuizPage.thirdPartyAssessment.noPasswordWarning"}
											components={{ b: <strong /> }}
											values={{ platform: selectedPlatform.platformName }}
										/>
									</Typography>
								</div>
							)}
						</>
					)}
				</div>
			</StepWrapper>
			<UnsupportedModal
				open={showUnsupportedModal}
				onClose={() => {
					setShowUnsupportedModal(false);
				}}
				onConfirm={() => {
					setProctoringOption?.({ disableRightClick: false });
					setShowUnsupportedModal(false);
				}}
				onEnableAnyway={() => {
					setEnableAnyway(true);
					setShowUnsupportedModal(false);
				}}
				platformUnsupportedOptionNames={{
					passwordConflict: {
						disableFeatures: [{ name: "Disable Right Click", option: "disableRightClick" }],
					},
				}}
				enableAnyway
			/>
		</>
	);
}

// TO DO DELETE BELOW WHEN NEW THIRD PARTY STEP IS DONE
const defaultValidateUrl = (url: string | undefined | null) => {
	if (url) {
		const isValidDomain = (url: string) => {
			const urlWithoutProtocol = url.replace(/^[a-zA-Z]+(?:[:/])*/, "");
			try {
				const { hostname } = new URL("https://" + urlWithoutProtocol);
				return /\.[a-zA-Z]{2,}$/.test(hostname);
			} catch (error) {
				return false;
			}
		};
		return {
			notEmpty: url.length > 0,
			noSpaces: !url.includes(" "),
			noTrailingSlash: !url.endsWith("/") && !url.endsWith("\\"),
			hasHttp: url.startsWith("http://") || url.startsWith("https://"),
			hasDomain: isValidDomain(url),
		};
	} else {
		return {
			notEmpty: false,
			noSpaces: false,
			noTrailingSlash: false,
			hasHttp: false,
			hasDomain: false,
		};
	}
};

type OldThirdPartyStepProps = {
	name: string | null;
	setName?: (name: string) => void;
	isThirdParty: boolean;
	thirdPartyUrl: string | undefined | null;
	setThirdPartyUrl: (url: string) => void;
	thirdPartyUrlInputRef?: HTMLInputElement;
	thirdPartyPassword: string | undefined | null;
	setThirdPartyPassword: (url: string) => void;
	setThirdPartySaveEnabled?: (enabled: boolean) => void;
	proctoringOption?: Partial<ProctoringOption>;
	setProctoringOption?: (options: Partial<ProctoringOption>) => void;
};

export function OldThirdPartyStep({
	thirdPartyUrl,
	setThirdPartyPassword,
	thirdPartyPassword,
	setThirdPartySaveEnabled,
	name,
	setName,
	thirdPartyUrlInputRef,
	setThirdPartyUrl,
	isThirdParty,
	proctoringOption,
	setProctoringOption,
}: OldThirdPartyStepProps) {
	const urlValidation = defaultValidateUrl(thirdPartyUrl);
	const allValidationsPassed = Object.values(urlValidation).every((v) => v);

	const [showUnsupportedModal, setShowUnsupportedModal] = useState(false);
	const [enableAnyway, setEnableAnyway] = useState(thirdPartyPassword ? true : false);
	const isMobileLayout = useMobileLayout();

	/* Enable save button if all validations have been passed */
	useEffect(() => {
		if (!allValidationsPassed) {
			setThirdPartySaveEnabled?.(false);
		} else {
			setThirdPartySaveEnabled?.(true);
		}
	}, [allValidationsPassed, setThirdPartySaveEnabled]);

	const hasNonWhitespace = (str: string | null | undefined) => typeof str === "string" && /\S/.test(str);

	const renderValidationImage = (isValid: boolean) => (
		<img src={isValid ? passImage : failImage} alt={isValid ? "Pass" : "Fail"} />
	);
	const nameId = useId();
	const urlId = useId();
	const passwordId = useId();

	return (
		<>
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => (
					<CorePaper component="section" className={styles.thirdPartyPaper}>
						{children}
					</CorePaper>
				)}
				whenFalse={(children) => <Box>{children}</Box>}
			>
				<Typography variant="body1" className={styles.urlTextLabel}>
					{setName
						? i18nPackage.t("editQuizPage.thirdPartyAssessment.old.pleaseEnterNameAndURL")
						: i18nPackage.t("editQuizPage.thirdPartyAssessment.old.pleaseEnterURL")}
				</Typography>
				<div className={`${styles.formGrid} ${isMobileLayout ? styles.formGridCompact : ""}`}>
					<InputLabel htmlFor={nameId}>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.nameLabel")}</InputLabel>
					<Tooltip
						title={
							!setName ? (
								<div className={styles.infoText}>
									<Typography>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.nameUnchangeable")}</Typography>
								</div>
							) : undefined
						}
					>
						<CoreTextField
							id={nameId}
							name="thirdPartyAssessmentName"
							value={name ?? ""}
							placeholder={i18nPackage.t("editQuizPage.thirdPartyAssessment.old.namePlaceholder")}
							disabled={!setName}
							onChange={setName ? (e) => setName(e.target.value) : undefined}
						/>
					</Tooltip>
					<div className={styles.requiredRow}>
						<InputLabel htmlFor={urlId}>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.urlLabel")}</InputLabel>
						{!thirdPartyUrlInputRef && (
							<Tooltip title={i18nPackage.t("editQuizPage.thirdPartyAssessment.old.urlRequired")}>
								<span>
									<CoreRequiredIcon width={12} height={12} />
								</span>
							</Tooltip>
						)}
					</div>
					<div className={`${styles.thirdPartyUrlControlContainer}`}>
						<Tooltip
							title={
								thirdPartyUrlInputRef ? (
									<div className={styles.infoText}>
										<p>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.urlDisabled")}</p>
									</div>
								) : undefined
							}
						>
							<CoreTextField
								id={urlId}
								name="thirdPartyAssessmentUrl"
								value={thirdPartyUrl}
								placeholder={i18nPackage.t("editQuizPage.thirdPartyAssessment.old.urlPlaceholder")}
								disabled={Boolean(thirdPartyUrlInputRef)}
								onChange={
									thirdPartyUrlInputRef
										? undefined
										: (e) => {
												const withoutLeadingWhitespace = e.target.value.trimStart();
												setThirdPartyUrl(withoutLeadingWhitespace);
											}
								}
							/>
						</Tooltip>
						{!allValidationsPassed && hasNonWhitespace(thirdPartyUrl) && (
							<div className={styles.urlValidationContainer} aria-live="polite">
								<div className={styles.validationRow}>
									{renderValidationImage(urlValidation.hasHttp)}
									<span>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.httpsError")}</span>
								</div>
								<div className={styles.validationRow}>
									{renderValidationImage(urlValidation.hasDomain)}
									<span>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.domainError")}</span>
								</div>
								<div className={styles.validationRow}>
									{renderValidationImage(urlValidation.notEmpty)}
									<span>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.emptyError")}</span>
								</div>
								<div className={styles.validationRow}>
									{renderValidationImage(urlValidation.noSpaces)}
									<span>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.spaceError")}</span>
								</div>
								<div className={styles.validationRow}>
									{renderValidationImage(urlValidation.noTrailingSlash)}
									<span>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.slashError")}</span>
								</div>
							</div>
						)}
						{allValidationsPassed && (
							<div className={styles.validUrlText}>
								<img src={passImage} alt={"Pass"} />
								<Typography>{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.validURL")}</Typography>
							</div>
						)}
					</div>
					{isThirdParty && (
						<>
							<InputLabel htmlFor={passwordId}>
								{i18nPackage.t("editQuizPage.thirdPartyAssessment.old.passwordLabel")}
							</InputLabel>

							<CoreTextField
								id={passwordId}
								name="thirdPartyAssessmentPassword"
								value={thirdPartyPassword ?? ""}
								placeholder={i18nPackage.t("editQuizPage.thirdPartyAssessment.passwordPlaceholder")}
								onChange={(e) => {
									if (
										proctoringOption?.disableRightClick &&
										setProctoringOption &&
										!enableAnyway &&
										!thirdPartyPassword
									) {
										setShowUnsupportedModal(true);
									} else {
										setThirdPartyPassword(e.target.value);
									}
								}}
							/>
						</>
					)}
				</div>
			</ConditionalWrapper>
			<UnsupportedModal
				open={showUnsupportedModal}
				onClose={() => {
					setShowUnsupportedModal(false);
				}}
				onConfirm={() => {
					setProctoringOption?.({ disableRightClick: false });
					setShowUnsupportedModal(false);
				}}
				onEnableAnyway={() => {
					setEnableAnyway(true);
					setShowUnsupportedModal(false);
				}}
				platformUnsupportedOptionNames={{
					passwordConflict: {
						disableFeatures: [{ name: "Disable Right Click", option: "disableRightClick" }],
					},
				}}
				enableAnyway
			/>
		</>
	);
}
